import React, { useEffect, useMemo, useState } from 'react';

import type { Options } from 'react-markdown';

import { useSiteNavigation } from 'AiAssistantWidget/Widget/contexts';

import { st, classes } from './Markdown.st.css';
import { transformMarkdown } from './markdownUtils';

const DummyMarkdown: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = (props) => {
  return <div className={props.className}>{props.children}</div>;
};

const InlineHeading: React.FC<React.PropsWithChildren> = ({ children }) => (
  <strong>{children}</strong>
);
const InlineImage: React.FC<{ alt?: string }> = ({ alt }) =>
  alt ? <em>({alt})</em> : null;

export const Markdown: React.FC<{
  text: string;
  className?: string;
  inline?: boolean;
}> = ({ text, className, inline }) => {
  const [remarkBreaks, setRemarkBreaks] = useState<() => void>();
  const { baseUrl } = useSiteNavigation();

  useEffect(() => {
    if (!remarkBreaks) {
      import('remark-breaks').then((module) => {
        setRemarkBreaks(() => module.default);
      });
    }
  }, [remarkBreaks]);

  const [rehypeMinifyWhitespace, setRehypeMinifyWhitespace] =
    useState<() => void>();

  useEffect(() => {
    if (!rehypeMinifyWhitespace) {
      import('rehype-minify-whitespace').then((module) => {
        setRehypeMinifyWhitespace(() => module.default);
      });
    }
  }, [rehypeMinifyWhitespace]);

  const [ReactMarkdown, setReactMarkdown] =
    useState<React.ComponentType<Options> | null>(null);

  useEffect(() => {
    if (!ReactMarkdown) {
      import('react-markdown').then((module) => {
        setReactMarkdown(() => module.default);
      });
    }
  }, [ReactMarkdown]);

  const processedText = useMemo(
    () => transformMarkdown(text, baseUrl),
    [text, baseUrl],
  );

  if (!ReactMarkdown || !remarkBreaks || !rehypeMinifyWhitespace) {
    return <DummyMarkdown className={className}>{text}</DummyMarkdown>;
  }

  return (
    <div className={className} data-hook="Markdown">
      <ReactMarkdown
        className={st(classes.root)}
        remarkPlugins={[remarkBreaks]}
        rehypePlugins={[rehypeMinifyWhitespace]}
        components={
          inline
            ? {
                h1: InlineHeading,
                h2: InlineHeading,
                h3: InlineHeading,
                h4: InlineHeading,
                h5: InlineHeading,
                h6: InlineHeading,
                img: InlineImage,
              }
            : null
        }
      >
        {processedText}
      </ReactMarkdown>
    </div>
  );
};
